import { useState, useCallback, useEffect } from 'react';
import { ethers } from 'ethers';
import { SIMPLE_SWAP_ADDRESS } from './constants';
import SimpleSwapABI from './abis/SimpleSwap.json';
import IERC20ABI from './abis/IERC20ABI.json';

const useSwap = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);

  // Check if wallet is already connected on component mount
  useEffect(() => {
const checkConnection = async () => {
  const userLoggedOut = localStorage.getItem('user_logged_out') === 'true';
  if (userLoggedOut) return; // Don't reconnect if explicitly logged out

  if (window.ethereum) {
    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        const address = await signer.getAddress();
        setAccount(address);
      }
    } catch (err) {
      console.error("Error checking existing wallet connection:", err);
    }
  }
};

    checkConnection();

    // Listen for account changes
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
        } else {
          setAccount(null);
        }
      });

      // Listen for chain changes
      window.ethereum.on('chainChanged', () => {
        window.location.reload();
      });
    }

    // Cleanup listeners
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', () => {});
        window.ethereum.removeListener('chainChanged', () => {});
      }
    };
  }, []);

  // Connect wallet function
  const connectWallet = useCallback(async () => {
    try {
      if (window.ethereum) {
        await window.ethereum.request({ method: 'eth_requestAccounts' });
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        
        // Check if the network is Sepolia
        const network = await provider.getNetwork();
        if (network.chainId !== 11155111) {
          try {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: '0xaa36a7' }], // 11155111 in hex
            });
          } catch (switchError) {
            if (switchError.code === 4902) {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{
                  chainId: '0xaa36a7',
                  chainName: 'Sepolia Test Network',
                  nativeCurrency: {
                    name: 'ETH',
                    symbol: 'ETH',
                    decimals: 18
                  },
                  rpcUrls: ['https://sepolia.infura.io/v3/'],
                  blockExplorerUrls: ['https://sepolia.etherscan.io']
                }]
              });
            }
          }
        }
        
        const signer = provider.getSigner();
        const address = await signer.getAddress();
        setAccount(address);
		localStorage.removeItem('user_logged_out'); // Clear the logout flag
        setError(null);
        return true;
      } else {
        setError('Please install MetaMask');
        return false;
      }
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  // Get token balance function - Fixed to handle decimals properly
  const getTokenBalance = useCallback(async (tokenAddress, walletAddress) => {
    try {
      if (!window.ethereum) return '0';
      
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function balanceOf(address) view returns (uint256)', 'function decimals() view returns (uint8)'],
        provider
      );
      
      const balance = await tokenContract.balanceOf(walletAddress);
      const decimals = await tokenContract.decimals();
      
      // Format with proper decimal places
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);
      
      // Return the raw value to preserve precision
      return formattedBalance;
    } catch (error) {
      console.error('Error getting token balance:', error);
      return '0';
    }
  }, []);

  // Get quote function using SimpleSwap contract
  const getQuote = useCallback(async (fromToken, toToken, amount) => {
    try {
      setLoading(true);
      setError(null);

      if (!window.ethereum) {
        throw new Error('Please install MetaMask');
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const simpleSwapContract = new ethers.Contract(
        SIMPLE_SWAP_ADDRESS,
        SimpleSwapABI,
        provider
      );

      // Parse the input amount
      const amountIn = ethers.utils.parseUnits(amount, fromToken.decimals);
      
      // Get the output amount from SimpleSwap contract
      try {
        const amountOut = await simpleSwapContract.getAmountOut(
          fromToken.address,
          toToken.address,
          amountIn
        );

        // Format the output amount
        const outputAmount = ethers.utils.formatUnits(amountOut, toToken.decimals);

        return {
          quote: outputAmount,
          route: {
            amountIn: amountIn,
            amountOut: amountOut,
            tokenIn: fromToken.address,
            tokenOut: toToken.address
          }
        };
      } catch (error) {
        console.error('Quote error:', error);
        // Check if tokens are supported or exchange rate is set
        if (error.message.includes('not supported')) {
          throw new Error('One or both tokens are not supported by this exchange');
        } else if (error.message.includes('Exchange rate not set')) {
          throw new Error('Exchange rate not set for this pair');
        } else {
          throw error;
        }
      }
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Execute swap using SimpleSwap contract
  const executeSwap = useCallback(async (route, fromToken) => {
    try {
      setLoading(true);
      setError(null);

      if (!window.ethereum) {
        throw new Error('Please install MetaMask');
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      
      // Create contract instances
      const simpleSwapContract = new ethers.Contract(
        SIMPLE_SWAP_ADDRESS,
        SimpleSwapABI,
        signer
      );

      const tokenContract = new ethers.Contract(
        fromToken.address,
        ['function approve(address spender, uint256 amount)', 'function allowance(address owner, address spender) view returns (uint256)'],
        signer
      );

      // Check allowance and approve if needed
      const currentAllowance = await tokenContract.allowance(account, SIMPLE_SWAP_ADDRESS);
      
      if (currentAllowance.lt(route.amountIn)) {
        console.log('Approving tokens...');
        const approveTx = await tokenContract.approve(SIMPLE_SWAP_ADDRESS, ethers.constants.MaxUint256);
        await approveTx.wait();
        console.log('Tokens approved');
      }

      // Execute the swap
      const minAmountOut = route.amountOut.mul(995).div(1000); // 0.5% slippage
      
      console.log('Executing swap...');
      const swapTx = await simpleSwapContract.swap(
        route.tokenIn,
        route.tokenOut,
        route.amountIn,
        minAmountOut
      );
      
      const receipt = await swapTx.wait();
      
      return {
        success: true,
        hash: receipt.transactionHash
      };
    } catch (err) {
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, [account]);
  
const logout = () => {
  setAccount(null);
  setLoading(false);
  setError('');
  localStorage.setItem('user_logged_out', 'true'); // Flag logout

  console.log('Wallet disconnected');
};

  return {
    account,
    loading,
    error,
    connectWallet,
    getQuote,
    executeSwap,
    getTokenBalance,
	logout 
  };
};

export default useSwap;